import { Directive, ElementRef, Input, OnChanges, OnInit, NgZone } from '@angular/core';

@Directive({
    selector: '[countTo]'
})
export class CountToDirective implements OnChanges, OnInit {
    @Input() countTo: number;
    @Input() duration: number = 0.3; // Duration in seconds

    private frameRate: number = 30; // Frame rate in FPS (default 60)
    private framesPerSecond: number = 1000 / this.frameRate; // Time per frame in ms
    private currentValue: number = 0; // Track the last displayed value

    constructor(
        private el: ElementRef,
        private zone: NgZone
    ) { }

    ngOnInit() {
        // Run entire logic outside Angular's zone to avoid triggering change detection
        this.zone.runOutsideAngular(() => {
            this.startCounting();
        });
    }

    ngOnChanges() {
        // Run the animation if the countTo value changes
        this.zone.runOutsideAngular(() => {
            if (this.countTo != undefined) {
                this.startCounting();
            }
        });
    }

    private startCounting() {
        const countFrom = this.currentValue; // Start from the current displayed value
        const target = this.countTo || 0; // Set the target value (default 100 if undefined)
        let duration = this.duration * 1000; // Convert to ms
        let steps = Math.ceil(duration / this.framesPerSecond); // How many steps per second
        let increment = (target - countFrom) / steps;

        let lastTime = 0; // Track the last time for each frame
        let frameCount = 0; // Count how many frames have passed

        // Ensure we're animating from the correct start value (whether it's going up or down)
        if (this.currentValue === 0) {
            this.currentValue = countFrom; // Ensure the start value is correct on the first run
        }

        const animate = (timestamp: number) => {
            if (!lastTime) lastTime = timestamp;
            const delta = timestamp - lastTime;

            // Only update at the frame interval
            if (delta >= this.framesPerSecond) {
                frameCount++;
                const easedValue = this.easeOutCubic(frameCount, countFrom, target - countFrom, steps);
                this.setContent(easedValue);

                if (frameCount >= steps) {
                    this.setContent(target); // Ensure we end exactly at target
                    this.currentValue = target; // Update the current value for next time
                    return; // Stop animation
                }

                lastTime = timestamp;
            }

            // Continue animation if not finished
            requestAnimationFrame(animate);
        };

        // Start the animation
        requestAnimationFrame(animate);
    }

    private setContent(value: number) {
        this.el.nativeElement.textContent = Math.round(value).toLocaleString();
    }

    // Cubic easing out function (ease-out)
    private easeOutCubic(t: number, b: number, c: number, d: number): number {
        t /= d;
        t--;
        return c * (t * t * t + 1) + b;
    }
}
